import React, { useState, useEffect } from 'react';
import axios from 'axios';
import useAxios from "axios-hooks";
import { Prompt, useHistory, useParams, useRouteMatch } from "react-router-dom";
import Select from "react-select";
import { FixedSizeList as List } from "react-window";
import { Formik, Field, Form, ErrorMessage } from "formik";
import CustomReactSelect from '../../../Forms/CustomReactSelect'
import * as Yup from "yup";
import {
  Stack,
  Text,
  Label,
  Dropdown,
  TextField,
  Checkbox,
  PrimaryButton,
  DefaultButton,
  MessageBar,
  MessageBarType,
  IconButton
} from '@fluentui/react';
import { drop } from 'lodash';
import FeatureSummary from '../../../Util/FeatureSummary';

const StepEdit = () => {
  let { stepId: stepId } = useParams();
  const history = useHistory();
  let { url } = useRouteMatch();
    const [conItems, setConItems] = useState([]);

  const validationSchema = (fieldVisibility) => Yup.object().shape({
    crsStepTitle: Yup.string().required("Step title is required"),
    crsStepTypeID: Yup.string().required("Step type is required"),
    crsPublishStatusID: Yup.string().required("Status is required"),
  
    header: Yup.string()
      .nullable()
      .when([], {
        is: () => fieldVisibility.showLikertSequences || fieldVisibility.showHeader,
        then: Yup.string().test("curly-braces", "Invalid {{{ }}} syntax", (value) => validateCurlyBraces(value)),
      }),
  
    paragraph: Yup.string()
      .nullable()
      .max(200, "Paragraph must be under 200 characters")
      .when([], {
        is: () => fieldVisibility.showParagraph || fieldVisibility.showAboutCRSPhrase,
        then: Yup.string().test("curly-braces", "Invalid {{{ }}} syntax", (value) => validateCurlyBraces(value)),
      }),
  
    button: Yup.string()
      .nullable()
      .when([], {
        is: () => fieldVisibility.showButton,
        then: Yup.string()
          .required("Button text is required")
          .test("curly-braces", "Invalid {{{ }}} syntax", (value) => validateCurlyBraces(value)),
      }),
  });
  
  const CustomMenuList = ({ children, ...props }) => {
    const height = 35;
    const itemCount = children.length;
    const totalHeight = Math.min(300, itemCount * height);

    return (
      <List height={totalHeight} itemCount={itemCount} itemSize={height} width="100%">
        {({ index, style }) => (
          <div
            style={{
              ...style,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              padding: "0 8px",
              boxSizing: "border-box",
            }}
            title={children[index]?.props?.children}
          >
            {children[index]}
          </div>
        )}
      </List>
    );
  };

  const [additional, setAdditional] = useState({
    addResList: [{ conid: null }],
    additionalCrsList: [{ crsid: null }],
  });

  const [dropdownData, setDropdownData] = useState({
    stepTypeList: [],
    crsList: [],
    interactiveExercizeList: [],
    conVideoList: [],
    conList: [],
    interactiveGraphics: [],
    infoGraphImageList: [],
    allCONLists: [],
    pubStatuses: [],
    formTypes: [],
    likertSequences: [],
  });

  const [fieldVisibility, setFieldVisibility] = useState({
    showIntEx: false,
    showCONID: false,
    showHeader: false,
    showParagraph: false,
    showOtherRes: false,
    showAboutCRSPhrase: false,
    showIntGraph: false,
    showAddRes: false,
    showForm: false,
    showLikertSequences: false,
    isQns: false,
    showButton: false,
    showCharacterCount: false,
    showInfographicArticleTextToggle: false,
  });

  const [discoverability, setDiscoverability] = useState([]);
  const [errorMsg, setErrorMsg] = useState('');

  const [{ data: dropdownDataResponse, loading: dropdownLoading, error: dropdownError }] = useAxios("courses/crs-step-data");
  const [{ data: discoverabilityResponse, loading: discoverabilityLoading, error: discoverabilityError }] = useAxios(`courses/step-discoverability/${stepId}`);
  const [{ data: stepResponse, loading: stepLoading, error: stepError }] = useAxios(`courses/crs-step/${stepId ? stepId : ''}`);
  const [{ data }, submitForm] = useAxios(
    { url: `courses/crs-step` },
    { manual: true }
  );

  useEffect(() => {
    if (dropdownDataResponse && !dropdownLoading) {
      setDropdownData({
        stepTypeList: dropdownDataResponse.crsStepTypes,
        crsList: dropdownDataResponse.crsList,
        interactiveExercizeList: dropdownDataResponse.interactiveExercises,
        conVideoList: dropdownDataResponse.conVideos,
        conList: dropdownDataResponse.coNs,
        interactiveGraphics: dropdownDataResponse.interactiveGraphics,
        infoGraphImageList: dropdownDataResponse.infoGraphImages,
        allCONLists: dropdownDataResponse.allCONLists,
        pubStatuses: dropdownDataResponse.publishStatuses,
        formTypes: dropdownDataResponse.forms,
        likertSequences: dropdownDataResponse.likertSequences
      });
    }
  }, [dropdownDataResponse, dropdownLoading]);

  useEffect(() => {
    if (discoverabilityResponse && !discoverabilityLoading) {
      setDiscoverability(discoverabilityResponse);
    }
  }, [discoverabilityResponse, discoverabilityLoading]);

  useEffect(() => {
    if (stepResponse && !stepLoading) {
      // Set additional resources and courses
      const updatedStep = {
        addResList: stepResponse.additionalResources?.map((res) => ({ conid: res.conid })) || [{ conid: '' }],
        additionalCrsList: stepResponse.additionalCRS?.map((crs) => ({ crsid: crs.crsid || '' })) || [{ crsid: '' }],
      };
      setAdditional(updatedStep);
    }
  }, [stepResponse, stepLoading]);

  // Only call showHideFields when both stepResponse and dropdownData are ready
  useEffect(() => {
    if (stepResponse && !stepLoading && dropdownData.stepTypeList.length) {
      if ([1, 18, 19].includes(stepResponse.crsStepTypeID)) {
        setFieldVisibility((prev) => ({ ...prev, isQns: true, showLikertSequences: true }));
      } else {
        const stepType = dropdownData.stepTypeList.find((item) => item.crsStepTypeID === stepResponse.crsStepTypeID);
        showHideFields(stepType?.crsStepTypeID ?? stepResponse.crsStepTypeID);
      }
    }
  }, [stepResponse, stepLoading, dropdownData]);

  const showHideFields = (stepTypeId) => {
    const visibility = {
      showIntEx: false,
      showCONID: false,
      showHeader: false,
      showParagraph: false,
      showOtherRes: false,
      showIntGraph: false,
      showAddRes: false,
      showForm: false,
      showLikertSequences: false,
      isQns: false,
      showButton: false,
      showCharacterCount: false,
      showInfographicArticleTextToggle: false,
    };

    switch (stepTypeId) {
      case 2:
        visibility.showCONID = true;
        visibility.showAddRes = true;
        setConItems(dropdownData.conVideoList || []);
        break;
      case 5:
      case 6:
        visibility.showHeader = true;
        visibility.showCharacterCount = true;
        visibility.showParagraph = true;
        visibility.showAddRes = true;
        break;
      case 25:
        visibility.showHeader = true;
        visibility.showCharacterCount = true;
        visibility.showParagraph = true;
        visibility.showAddRes = false;
        break;
      case 8:
        visibility.showIntEx = true;
        visibility.showAddRes = true;
        break;
      case 9:
        visibility.showCONID = true;
        visibility.showAddRes = true;
        visibility.showInfographicArticleTextToggle = true;
        setConItems(dropdownData.infoGraphImageList || []);
        break;
      case 10:
        visibility.showIntGraph = true;
        visibility.showAddRes = true;
        break;
      case 11:
        visibility.showOtherRes = true;
        visibility.showAddRes = true;
        visibility.showAboutCRSPhrase = true;
        setConItems(dropdownData.allConList || []);
        break;
      case 23: //Form
        visibility.showForm = true;
        visibility.showCONID = false;
        break;
      case 15:
        visibility.showCONID = true;
        visibility.showAddRes = true;
        setConItems(dropdownData.conList || []);
        break;
      case 24:
        visibility.showLikertSequences = true;
        setConItems(dropdownData.conList || []);
        break;
      case 28:
        visibility.showHeader = true;
        visibility.showParagraph = true;
        visibility.showAddRes = true;
        visibility.showButton = true;
        break;
      case 1:
      case 18:
      case 19:
        visibility.showLikertSequences = true;
        visibility.isQns = true;
        setConItems(dropdownData.conList || []);
        break;
      default:
        visibility.showAddRes = true;
    }

    setFieldVisibility(visibility);
  };

  const addResource = () => {
    setAdditional((prev) => {
      return ({
        ...prev,
        addResList: [...prev.addResList, { conid: '' }],
      })
    });
  };

  const removeResource = (index) => {
    setAdditional((prev) => {
      const updatedList = [...prev.addResList];
      updatedList.splice(index, 1);
      return { ...prev, addResList: updatedList };
    });
  };

  const addAdditionalCrs = () => {
    setAdditional((prev) => ({
      ...prev,
      additionalCrsList: [...prev.additionalCrsList, { crsid: '' }],
    }));
  };

  const removeAdditionalCrs = (index) => {
    setAdditional((prev) => {
      const updatedList = [...prev.additionalCrsList];
      updatedList.splice(index, 1);
      return { ...prev, additionalCrsList: updatedList };
    });
  };

  const buildAdditonalResources = () => {
    if (additional.addResList.length === 1 && additional.addResList[0].conid === '') {
      return;
    }
    return additional.addResList.filter(x => x.conid).map(item => ({
      CONID: item.conid,
      CRSStepAdditionalResourceID: null,
    }));
  };

  const buildAdditonalCRSs = () => {
    if (additional.additionalCrsList.length === 1 && additional.additionalCrsList[0].crsid === '') {
      return;
    }
    return additional.additionalCrsList.filter(x => x.crsid).map(item => ({
      CRSID: item.crsid,
      CRSStepAdditionalCRSID: null,
    }));
  };

  const validateCurlyBraces = (str, location) => {
    if (str == null) {
      return;
    }
    const begin = str.split('{{{');
    const end = str.split('}}}');
    if (begin.length !== end.length) {
      return false;
    }else{
      return true;
    }
  };

  return (
    <Stack tokens={{ childrenGap: 15 }} style={{ padding: 20, width: 500 }}>
    {!stepLoading  && <Formik
      initialValues={{
        crsStepId:stepResponse.crsStepID || "",
        crsStepTypeID: stepResponse.crsStepTypeID || "",
        crsStepTitle: stepResponse.crsStepTitle || "",
        primaryCRSID: stepResponse.primaryCRSID || "",
        crsPublishStatusID: stepResponse.crsPublishStatusID || "",
        header: stepResponse.header || "",
        paragraph: stepResponse.paragraph || "",
        button: stepResponse.button || "",
        hideDisclaimer: stepResponse.hideDisclaimer || false,
        showInfographicArticleText: stepResponse.showInfographicArticleText || false,
        formID: stepResponse.formID || "",
        conid: stepResponse.conid || "",
        conInteractiveID: stepResponse.conInteractiveID || "",
        crsBecauseOfPhrase: stepResponse.crsBecauseOfPhrase || "",
      }}
      validationSchema={() => validationSchema(fieldVisibility)} 
      onSubmit={async (values, { setSubmitting, setStatus }) => {
        const stepObj = {
          ...values,
          CONInteractiveID: fieldVisibility.showIntGraph
            ? values.conInteractiveID
            : fieldVisibility.showIntEx
            ? values.conInteractiveID
            : null,
          AdditionalResources: buildAdditonalResources(), 
          AdditionalCRS: buildAdditonalCRSs(), 
        };
        if (fieldVisibility.showForm) {
          stepObj.FormID = values.formID;
        } else if (fieldVisibility.showIntEx) {
          stepObj.FormID = values.formID;
        }
        const result = await submitForm({
          data: stepObj,
          method: "post",
          url: "courses/crs-step",
        });
        if (!result.isAxiosError) {
          setStatus({ success: "Step has been saved successfully!" });
          if(!stepObj.crsStepId){
            location.href = `${url}${result.data}`;
          }
        } else {
          setStatus({ error: "An error occurred while saving the step." });
        }
        setSubmitting(false);
      }}
    >
      {({ handleChange, values, status, setFieldValue }) => (
      <Form>
      <div className='displayflex aligncenter' style={{ justifyContent: 'space-between' }}>
        <Text variant="xLarge" style={{ color: 'green' }}>
          Steps
        </Text>
        <PrimaryButton type="submit">Save</PrimaryButton>
      </div>

      {status?.success && (
        <MessageBar messageBarType={MessageBarType.success} isMultiline={false}>
          {status.success}
        </MessageBar>
      )}
      {status?.error && (
        <MessageBar messageBarType={MessageBarType.error} isMultiline={false}>
          {status.error}
        </MessageBar>
      )}

      {errorMsg && (
        <MessageBar
          messageBarType={MessageBarType.error}
          isMultiline={false}
          onDismiss={() => setErrorMsg('')}
        >
          {errorMsg}
        </MessageBar>
      )}
        <Stack tokens={{ childrenGap: 10 }}>
          <Stack.Item>
            <Label>Select Type*</Label>
            {fieldVisibility.isQns ? (
              <TextField value="Likert Sequence" disabled />
            ) : (
              <Field
                name="crsStepTypeID"
                component={CustomReactSelect}
                options={dropdownData.stepTypeList?.map((item) => ({
                  value: item.crsStepTypeID,
                  label: item.name,
                })) || []}
                placeholder="Select a step type"
                className="flex1"
                required
                onChange={(value) => {
                  showHideFields(value);
                }}
              />
            )}
          <ErrorMessage name="crsStepTypeID" component="div" className="error-message" />
          </Stack.Item>
          <Stack.Item>
            <Label>Step Title*</Label>
            <TextField
              required
              placeholder="Enter Step Title"
              value={values.crsStepTitle}
              onChange={handleChange("crsStepTitle")}
            />
            <Text variant="small">{values.crsStepTitle?.length ? values.crsStepTitle?.length : 0} / 45 Characters</Text>
            <ErrorMessage name="crsStepTitle" component="div" className="error-message" />
          </Stack.Item>

          <Stack.Item>
            <Label>Primary Course</Label>
            <Field
              name="primaryCRSID"
              component={CustomReactSelect}
              options={dropdownData.crsList?.map((item) => ({
                value: item.crsid,
                label: item.title + (item.crsid ? ` / Course Id: ${item.crsid}` : ''),
              })) || []}
              placeholder="Select a primary course"
              className="flex1"
              isSearchable={true} // Enables search functionality
            />
          </Stack.Item>
          <Stack.Item>
            <Label>Status*</Label>
            <Field
                name="crsPublishStatusID"
                component={CustomReactSelect}
                options={dropdownData.pubStatuses?.map((item) => ({
                  value: item.crsPublishStatusID,
                  label: item.name,
                })) || []}
                placeholder="Select a status"
                className="flex1"
                required
              />
            <ErrorMessage name="crsPublishStatusID" component="div" className="error-message" />
          </Stack.Item>
          {fieldVisibility.showLikertSequences && (
            <Stack tokens={{ childrenGap: 10 }}>
              <Label>Likert Sequence (Header | Question)*</Label>
              <TextField
                required
                placeholder="Enter Likert Sequence Header | Question"
                value={values.header}
                onChange={handleChange("header")}
                />
              <ErrorMessage name="header" component="div" className="error-message" />
            </Stack>
          )}
          {fieldVisibility.showCONID && (
            <Stack>
              <Label>CONID*</Label>
              <Field
                name="conid"
                component={CustomReactSelect}
                options={conItems?.map((con) => ({
                  value: con.conid,
                  label: `${con.conid}: ${con.title}`,
                })) || []}
                placeholder="Select CONID"
                className="flex1"
                isSearchable={true} // Enables search functionality
              />
              {values.conid && (
                <Stack horizontal className='mt10'>
                  {values.conid >= 1100000 ? (
                    <a href={`/Videos/Add/${values.conid}`} target="_blank" rel="noopener noreferrer">
                      Go to Videos CMS
                    </a>
                  ) : (
                    <a href={`/Contents/Add/${values.conid}`} target="_blank" rel="noopener noreferrer">
                      Go to Content CMS
                    </a>
                  )}
                </Stack>
              )}
            </Stack>
          )}
          {fieldVisibility.showCONID && (<Stack>
            <Checkbox
              label="Hide disclaimer"
              checked={values.hideDisclaimer}
              onChange={(e, checked) => setFieldValue("hideDisclaimer", checked)}
            />
          </Stack>)}
          {fieldVisibility.showInfographicArticleTextToggle && (<Stack>
            <Checkbox
              label="Show article text with infographics"
              checked={values.showInfographicArticleText}
              onChange={(e, checked) => setFieldValue("showInfographicArticleText", checked)}
            />
          </Stack>)}
          {fieldVisibility.showForm && (
            <Stack >
              <Label>Form Type</Label>
              <Field
                name="formID"
                component={CustomReactSelect}
                options={dropdownData.formTypes?.map((form) => ({
                  value: form.formID,
                  label: form.name,
                })) || []}
                placeholder="Select a form type"
                className="flex1"
                isSearchable={true} // Enables search functionality
              />
            </Stack>
          )}

          {fieldVisibility.showHeader && (
            <Stack.Item>
              <Label>Header</Label>
              <TextField
                placeholder="Enter Header"
                value={values.header || ''}
                onChange={handleChange("header")}
              />
              <ErrorMessage name="header" component="div" className="error-message" />
            </Stack.Item>
          )}

          {fieldVisibility.showParagraph && (
            <Stack>
              <Label>Paragraph(s)</Label>
              <TextField
                placeholder="Enter paragraph content"
                multiline
                rows={3}
                value={values.paragraph}
                onChange={handleChange("paragraph")}
              />
              {fieldVisibility.showCharacterCount && (
                <Text variant="small">{values.paragraph?.length ? values.paragraph?.length : 0} / 200 Characters</Text>
              )}
              <ErrorMessage name="paragraph" component="div" className="error-message" />
            </Stack>
          )}

          {fieldVisibility.showButton && (
            <Stack>
              <Label>Button</Label>
              <TextField
                required
                placeholder="Enter button text"
                value={values.button}
                onChange={handleChange("button")}
              />
              <ErrorMessage name="button" component="div" className="error-message" />
            </Stack>
          )}

          {fieldVisibility.showAboutCRSPhrase && (
            <Stack>
              <Label>Course primarily about (phrase)*</Label>
              <TextField
                required
                placeholder="Enter course phrase"
                value={values.paragraph}
                onChange={handleChange("paragraph")}
              />
              <ErrorMessage name="paragraph" component="div" className="error-message" />
            </Stack>
          )}

          {fieldVisibility.showOtherRes && (
            <Stack>
              <Label>Course because of (phrase)*</Label>
              <TextField
                required
                placeholder="Enter reason for course"
                value={values.crsBecauseOfPhrase}
                onChange={handleChange("crsBecauseOfPhrase")}
              />
              <ErrorMessage name="crsBecauseOfPhrase" component="div" className="error-message" />
            </Stack>
          )}

          {fieldVisibility.showOtherRes && (<Stack>
            <Stack>
              <Label>Other Courses</Label>
              {additional.additionalCrsList?.map((item, index) => {
                return (
                  <Stack horizontal tokens={{ childrenGap: 10 }} className='mb20' key={index}>
                    <Field
                      name={`additional.additionalCrsList[${index}].crsid`}
                      component={CustomReactSelect}
                      options={dropdownData.crsList?.map((crs) => ({
                        value: crs.crsid,
                        label: crs.title,
                      })) || []}
                      placeholder="Select a course"
                      className="flex1"
                      isSearchable={true} // Enables search functionality
                      value={ item.crsid }
                      onChange={(selectedOption) => {
                        const updatedList = [...additional.additionalCrsList];
                        updatedList[index].crsid = selectedOption;
                        setAdditional({ ...additional, additionalCrsList: updatedList });
                      }}
                    />
                    <IconButton
                      iconProps={{ iconName: 'Delete' }}
                      title="Delete"
                      ariaLabel="Delete"
                      onClick={() => removeAdditionalCrs(index)}
                    />
                  </Stack>
                )
              })}
              <DefaultButton text="Add Additional Course +" onClick={addAdditionalCrs} />
            </Stack>
          </Stack>)}

          {fieldVisibility.showIntEx && (
            <Stack >
              <Label>Interactive Exercise*</Label>
              <Field
                  name="conInteractiveID"
                  component={CustomReactSelect}
                  options={dropdownData.interactiveExercizeList?.map((intEx) => ({
                    value: intEx.conInteractiveID,
                    label: intEx.name,
                  })) || []}
                  placeholder="Select an interactive exercise"
                  className="flex1"
                  isSearchable={true} // Enables search functionality
                  required
                />
              <ErrorMessage name="conInteractiveID" component="div" className="error-message" />
            </Stack>
          )}

          {fieldVisibility.showIntGraph && (
            <Stack >
              <Label>Interactive Graphic*</Label>
              <Field
                  name="conInteractiveID"
                  component={CustomReactSelect}
                  options={dropdownData.interactiveGraphics?.map((intGraph) => ({
                    value: intGraph.conInteractiveID,
                    label: intGraph.name,
                  })) || []}
                  placeholder="Select an interactive graphic"
                  className="flex1"
                  isSearchable={true} // Enables search functionality
                />
              <ErrorMessage name="conInteractiveID" component="div" className="error-message" />
            </Stack>
          )}
          {fieldVisibility.showAddRes && (<Stack.Item>
            <Label>Additional Resources</Label>
            {additional.addResList?.map((res, index) => {
              return (
                <Stack horizontal tokens={{ childrenGap: 10 }} style={{ marginBottom: 5 }} key={index}>
                  <Field
                      name={`additional.addResList[${index}].conid`}
                      component={CustomReactSelect}
                      components={{ MenuList: CustomMenuList }}
                      options={dropdownData.allCONLists?.map((item) => ({
                        value: item.conid,
                        label: `${item.conid}: ${item.title ? item.title : ''}`,
                      })) || []}
                      placeholder="Select a resource"
                      className="flex1"
                      isSearchable
                      value={res.conid }
                      onChange={(value) => {
                        const updatedList = [...additional.addResList];
                        updatedList[index].conid = value;
                        setAdditional((prev) => ({ ...prev, addResList: updatedList }));
                      }}
                    />
                  <DefaultButton
                    text="Remove"
                    onClick={() => removeResource(index)}
                  />
                </Stack>
              )
            })}
            <PrimaryButton className="mt10" text="Add Resource" onClick={addResource} />
          </Stack.Item>)}

          <Stack.Item>
            <Label>Discoverability</Label>
            <div className='flexcolumn'>
              {discoverability.length > 0 ? (
                discoverability?.map((course, index) => (
                  <div key={index} style={{ marginBottom: 5 }}>
                    CRSID: {course.crsid} | {course.stepCategoryName} | Step: {course.stepNumber} |{' '}
                    <a href={`/Courses/Edit/${course.crsid}`} target="_blank" rel="noopener noreferrer">
                      {course.title}
                    </a>
                  </div>
                ))
              ) : (
                <li><i>None Found</i></li>
              )}
            </div>
          </Stack.Item>
        </Stack>
        <FeatureSummary
            label="About Course Steps"
            summary="Create or edit course steps with various content types including headers, paragraphs, videos, interactive exercises, infographics, and more. Configure step type, publish status, and associate content resources. View which courses include this step in the Discoverability section."
        />
        </Form>
      )}
    </Formik>    }
    </Stack>
  );
};


export default StepEdit;
